/**
 * 矩阵视图「信息列」表头 + 列宽 + 列头口径说明（2026-10-29 新增）。
 *
 * ── 需求背景 ────────────────────────────────────────────────────────────────
 * IT 两份逾期文档（`Ali系统-项目问题汇总-20261029.xlsx` / `Ali系统_IT_Overdue_Report_20261029.xlsx`）
 * 反复点名两个误读：
 *   ① 「标准结束日 超期 810 天」被当成「当前阶段延误了 810 天」
 *      （37 Huntington / 306 Jackson 两行）；
 *   ② 「节点」与「阶段预计」「承诺日」被当成同一个日期
 *      （全局工期设置那一行要求「核对 Standard / Committed / Stage forecast 的取值」）。
 * 2026-10-29 提交 cac1126f 先给**表格视图**的列头补了常驻 ⓘ + 悬停 title，
 * 但矩阵视图的列头仍是裸的列名 —— 切到矩阵后这两个误读还是没有任何解释入口。
 * 用户 2026-10-29 明确要求「给矩阵加上」，故有本文件。
 *
 * ── 为什么单独成一个文件 ────────────────────────────────────────────────────
 * ① 矩阵表头原先内联在 `MatrixView` 里，而 `MatrixView` 依赖 api / meta /
 *    十几条派生数据，单测要 mock 一堆东西才能验一句「列头有没有 ⓘ」；
 *    这里要守的恰恰只是一条纯渲染规则：**有 title 的列必须同时有可见的 ⓘ**。
 * ② `DesignV3Projects.jsx` 顶层 import 了 ESM-only 的 `react-router-dom` v7，
 *    jest 在 CRA 默认配置下解析不了，任何 import 该模块的测试都会直接报
 *    `Cannot find module 'react-router-dom'`。抽到独立文件后（本文件只依赖
 *    `lucide-react` 与 `lib/i18n`），渲染规则可以真正跑起来做回归。
 *
 * ── 边界（刻意不做的事）─────────────────────────────────────────────────────
 * · 列宽、可见列、testid 生成规则全部保持与原实现逐字一致，矩阵表体
 *   （`dv3-mx-info-td-*`）与列设置隐藏逻辑一行未动。
 * · 只有 `deadline` / `std` 两列有说明；其余信息列走的是与改造前完全相同的
 *   输出（`<th>` 里只有一个文本节点，不套 span、不加图标），零影响。
 * · 不给任何列加筛选入口 —— `std` 是「给老板看的合同尺子」，一旦可筛选就会
 *   被当成「逾期项目」入口，把 82 天重新变回业务判定口径（2026-10-27 定调）。
 */
import React from "react";
import { Info } from "lucide-react";
import { T } from "../../../lib/i18n";

/**
 * 列头口径说明（表格视图与矩阵视图**共用同一份文案**）。
 *
 * 为什么抽成常量：同一句话要在四处出现 —— 表格视图的 `ThFilter`（title）、
 * 表格视图 `plain` 列的 `TableHead`、快速筛选条 chip、矩阵视图列头。
 * 原先直接内联在 `columnDefs` 里，矩阵那一份根本取不到（这正是本次补矩阵 ⓘ
 * 时才发现的半截状态：改了表格忘了矩阵）。
 *
 * 文案内容与提交 cac1126f 完全一致，一个字未改。
 */
export const COL_TITLE = {
  deadline: "阶段预计完成日 = 当前未完成阶段里最晚的那个截止日，会随改期 / City 退图 / 测绘顺延一起变。它与「标准结束日」（签约日 + 82 美国工作日的合同基准）不是同一个口径；承诺日（Committed End Date，由延期审批治理）在项目详情的「管理历史承诺日与延期审批」里查看，不在本表列内。",
  std: "合同基准：签约日 + 82 个美国工作日，不随阶段顺延变化。这里的「超期」是合同基准偏差，与「节点」列（当前阶段排期）是两个不同口径，不代表当前阶段或设计部的延误。",
};

/**
 * 矩阵信息列宽（2026-10-19 建立，2026-10-23 按英文实测宽度重算，2026-10-27 加 std）。
 *
 * 原始痛点（用户截图「英文情况下会超出」）：切到英文后，工作状态 / 节点 / 分公司三列的
 * 文字**画到格子外面、压在右侧相邻列上**，看起来像两列文字糊在一起。
 *
 * 根因：这些列宽是 2026-10-19 按**中文**文案定的（中文「待现场/土地测量」7 个字 ≈ 108px、
 * 「逾期 30 天 · 09/23/2026」≈ 140px、中文分公司名 4 个字 ≈ 44px）。而英文同一个状态是
 * 「Pending Site & Land Survey」(172px)、节点是「Overdue by 30 Days · 09/23/2026」(196px)、
 * 分公司是「NorCal Bay Area」(87px)，普遍比中文宽 40%~100%。在 `table-fixed` 下 `td`
 * 宽度是写死的、内部又是 `whitespace-nowrap`，超出的部分直接溢出到单元格之外 ——
 * 单元格本身没变宽，是文字「越界」了。实测越界量：状态 45.9px、节点 41.0px、分公司 20.5px
 * （1440×900，英文界面，admin）。
 *
 * 修法（宽度按「英文最坏情况 + 左右内边距」重算，而不是按中文）：
 * 每个值 = 实测最长内容宽度 + 单元格 px-1.5 的左右各 6px + 少量余量；取值来源是真实
 * 渲染测量，不是按字符数估算（中英混排、字重、tabular-nums 都会影响宽度）。
 *
 * 「耗时」为什么也要放宽到 142：真实数据里已存在 30 个月的项目（≈923 天），英文文案是
 * 「30 months 31 days」(实测 130px)，原 104px 会让它折成两行、把该行行高从 72px 顶高，
 * 破坏矩阵逐行对齐。142 是「英文最坏情况 130 + 内边距 12」的实测值。
 *
 * 为什么是「加宽」而不是「截断省略」：这一列的全部价值就是让人读到状态名和逾期天数，
 * 英文界面把「Pending Site & Land Survey」截成「Pending Site & L…」等于信息丢失，
 * 而矩阵本来就横向滚动，加宽只是多滚一点点，换来两种语言都能读全。
 *
 * std（标准结束日）文案只有两种形态：未超期「09/23/2026」（≈76px）、超期
 * 「超期 809 天 · 08/09/2024」（英文「Overdue 809 days · 08/09/2024」实测约 185px）。
 * 取 196 = 185 + 左右内边距 12，与 deadline 列同一套「按英文最坏情况定宽」的口径。
 */
export const MATRIX_INFO_WIDTH = { branch: 104, status: 188, prog: 100, sign: 142, owner: 132, deadline: 212, std: 196 };

/**
 * 矩阵列头说明映射：键是信息列 key，值是 `COL_TITLE` 的键。
 *
 * 为什么用映射而不是直接把 title 塞进 `CONFIG_COLS`：`CONFIG_COLS` 的元素是
 * `[key, 中文列名]` 二元组，被列设置开关、矩阵表头、tbody 三处共用；改成三元组
 * 会连带改三处解构（且容易漏掉某一处，让该处拿到 `undefined`）。多一层映射
 * 只影响表头渲染，其余调用方一行不用动。
 *
 * 注意矩阵这里「阶段预计」的列名叫「节点」（`CONFIG_COLS` 沿用旧名），所以 ⓘ 文案里
 * 「与「节点」列（当前阶段排期）」在两个视图都自洽：表格视图里它叫「阶段预计」，
 * 矩阵里它就是被 IT 点名的那个「节点」。
 */
export const MATRIX_COL_TITLE = { deadline: "deadline", std: "std" };

/**
 * 矩阵信息列表头格。
 *
 * @param {string} colKey 信息列 key（`CONFIG_COLS` 的 key）
 * @param {string} label  列名（中文原文，渲染时经 `T()` 翻译）
 * @returns {JSX.Element} 一个吸顶 `<th>`；有说明的列额外带常驻 ⓘ 与 `title`
 *
 * 有说明的列 vs 没说明的列在 DOM 上刻意不同：
 *   有 → `<span><text><Info/></span>`（便于对齐图标、也给出 `-info` testid 供测试断言）
 *   无 → 直接一个文本节点（与改造前逐字一致，保证其余列零影响）
 */
export const MatrixInfoTh = ({ colKey, label }) => {
  const note = MATRIX_COL_TITLE[colKey];
  return (
    <th data-testid={`dv3-mx-info-th-${colKey}`} data-title={note ? "1" : undefined} title={note ? T(COL_TITLE[note]) : undefined}
      style={{ width: MATRIX_INFO_WIDTH[colKey] }} className="sticky top-7 z-[2] bg-slate-50 px-1.5 py-2 text-left text-[10px] font-bold text-slate-500 whitespace-nowrap border-b border-slate-200">
      {note
        ? <span className="inline-flex items-center gap-1">{T(label)}<Info data-testid={`dv3-mx-info-th-${colKey}-info`} className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" /></span>
        : T(label)}
    </th>
  );
};
